import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import FormFieldRenderer from '../FormFieldRenderer';
import { ItemResponse } from '../../utils/logEntryUtils';
import { LogTemplate, LogTemplateField } from './LogTemplateBuilder';

interface LogEntryFormProps {
  template: LogTemplate;
  initialOccurredAt?: string;
  initialResponses?: Record<number, ItemResponse>;
  readOnly?: boolean;
  submitLabel?: string;
  onSubmit?: (data: { occurred_at: string; responses: Record<number, ItemResponse> }) => Promise<void>;
}

const toLocalInputValue = (value?: string) => {
  if (!value) {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  }
  const date = new Date(value);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

const fieldToItem = (field: LogTemplateField) => ({
  id: field.id || 0,
  title: field.title,
  description: field.description,
  item_type: field.item_type,
  order: field.order,
  is_required: field.is_required,
  validation_rules: field.validation_rules,
  options: field.options || [],
  default_value: field.default_value || '',
  allowed_file_types: field.allowed_file_types || '',
  max_file_size_mb: field.max_file_size_mb || 10,
  is_visible: field.is_visible !== false,
  show_when_parent_completed: field.show_when_parent_completed || false,
});

const LogEntryForm: React.FC<LogEntryFormProps> = ({
  template,
  initialOccurredAt,
  initialResponses,
  readOnly = false,
  submitLabel = 'Submit Log',
  onSubmit,
}) => {
  const [occurredAt, setOccurredAt] = useState(toLocalInputValue(initialOccurredAt));
  const [responses, setResponses] = useState<Record<number, ItemResponse>>(initialResponses || {});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sortedFields = useMemo(
    () => [...(template.fields || [])].sort((a, b) => a.order - b.order),
    [template.fields],
  );

  const updateResponse = (fieldId: number, response: any) => {
    setResponses((prev) => ({
      ...prev,
      [fieldId]: {
        item_id: fieldId,
        response,
        notes: prev[fieldId]?.notes || '',
        cannot_complete: prev[fieldId]?.cannot_complete || false,
        cannot_complete_reason: prev[fieldId]?.cannot_complete_reason || '',
      },
    }));
  };

  const updateNotes = (fieldId: number, notes: string) => {
    setResponses((prev) => ({
      ...prev,
      [fieldId]: {
        item_id: fieldId,
        response: prev[fieldId]?.response ?? '',
        notes,
        cannot_complete: prev[fieldId]?.cannot_complete || false,
        cannot_complete_reason: prev[fieldId]?.cannot_complete_reason || '',
      },
    }));
  };

  const updateCannotComplete = (fieldId: number, cannotComplete: boolean, reason: string) => {
    setResponses((prev) => ({
      ...prev,
      [fieldId]: {
        item_id: fieldId,
        response: prev[fieldId]?.response ?? '',
        notes: prev[fieldId]?.notes || '',
        cannot_complete: cannotComplete,
        cannot_complete_reason: reason,
      },
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!onSubmit || readOnly) return;

    for (const field of sortedFields) {
      if (!field.is_required || !field.id) continue;
      const response = responses[field.id];
      const value = response?.response;
      const missing =
        value === undefined ||
        value === null ||
        value === '' ||
        (field.item_type === 'checkbox' && !value);
      if (missing && !response?.cannot_complete) {
        setError(`"${field.title}" is required.`);
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      const occurredIso = new Date(occurredAt).toISOString();
      await onSubmit({ occurred_at: occurredIso, responses });
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to save log entry');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TextField
        fullWidth
        type="datetime-local"
        label="Occurred At"
        value={occurredAt}
        onChange={(e) => setOccurredAt(e.target.value)}
        InputLabelProps={{ shrink: true }}
        disabled={readOnly}
        sx={{ mb: 2 }}
      />

      {sortedFields.length === 0 ? (
        <Alert severity="warning">This log template has no fields configured.</Alert>
      ) : (
        sortedFields.map((field) => {
          if (!field.id) return null;
          return (
            <Box key={field.id} sx={{ mb: 2 }}>
              <FormFieldRenderer
                item={fieldToItem(field)}
                response={responses[field.id]}
                onResponse={updateResponse}
                onNotes={updateNotes}
                onCannotComplete={updateCannotComplete}
                isReadOnly={readOnly}
              />
            </Box>
          );
        })
      )}

      {!readOnly && onSubmit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : submitLabel}
          </Button>
        </Box>
      )}

      {readOnly && sortedFields.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Read-only view
        </Typography>
      )}
    </Box>
  );
};

export default LogEntryForm;
